import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Undo, DeleteOutline, LockReset } from "@mui/icons-material";
import Input from "../Input";
import { useAuth } from "../../context/AuthContext";
import ServiceTierSelect from "./ServiceTierSelect";
import axios from "axios";

function ProfileForm({ onNotification }) {
  const { user, deleteProfile, resetPassword } = useAuth();
  
  // Initialize form data with user data
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    companyName: "",
    vatNumber: "",
    serviceTier: "free"
  });
  
  const [status, setStatus] = useState({ success: false, error: null });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);

  // Load user data once on component mount
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || "",
        email: user.email || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        companyName: user.companyName || "",
        vatNumber: user.vatNumber || "",
        serviceTier: user.serviceTier || "free"
      });
    }
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate email
    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (
      !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Validate VAT number if provided
    if (formData.vatNumber && !/^[A-Z]{2}[0-9A-Z]+$/.test(formData.vatNumber)) {
      newErrors.vatNumber = "Please enter a valid VAT number format (e.g., DE123456789)";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field-specific error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleServiceTierChange = (newTier) => {
    setFormData(prev => ({
      ...prev,
      serviceTier: newTier
    }));
    setStatus({ success: true, error: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      onNotification(
        'Validation Error',
        'Please check all required fields and try again.',
        'error'
      );
      return;
    }

    setLoading(true);
    setStatus({ success: false, error: null });

    try {
      await axios.put('/api/auth/profile', formData);
      onNotification(
        'Profile Updated',
        'Your profile information has been successfully updated.',
        'success'
      );
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to update profile';
      setStatus({
        success: false,
        error: errorMsg,
      });
      onNotification(
        'Failed to Update Profile',
        errorMsg,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Clear success message after a delay
  useEffect(() => {
    let timer;
    if (status.success) {
      timer = setTimeout(() => {
        setStatus((prev) => ({ ...prev, success: false }));
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [status.success]);

  function handleDeleteProfile() {
    setOpenDeleteDialog(true);
  }

  function handleConfirmDelete() {
    setLoading(true);
    deleteProfile()
      .then(result => {
        if (result && result.success) {
          onNotification(
            'Profile Deleted',
            'Your profile has been successfully deleted.',
            'success'
          );
          // Redirect to login page after successful deletion
          window.location.href = '/login';
        }
      })
      .catch(err => {
        onNotification(
          'Failed to Delete Profile',
          err.message || 'An error occurred while deleting your profile.',
          'error'
        );
      })
      .finally(() => {
        setLoading(false);
        setOpenDeleteDialog(false);
      });
  }

  function handleCancelDelete() {
    setOpenDeleteDialog(false);
  }

  function handleResetPassword(e) {
    setOpenResetPasswordDialog(true);
    e.preventDefault();
  }

  function handleConfirmResetPassword(e) {
    resetPassword(formData.email);
    setOpenResetPasswordDialog(false);
    e.preventDefault();
  }

  function handleCancelResetPassword() {
    setOpenResetPasswordDialog(false);
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
      <Typography variant="h6" gutterBottom>
        Edit Profile Information
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Username and email are obligatory fields, and username cannot be
        changed. Other fields are optional.
      </Typography>

      {(status.success || status.error) && (
        <Alert
          severity={status.success ? "success" : "error"}
          sx={{ mb: 2, width: "100%" }}
          onClose={() => setStatus({ success: false, error: null })}
        >
          {status.success ? "Profile updated successfully!" : status.error}
        </Alert>
      )}

      <Stack spacing={3}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Input
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            disabled // Username typically shouldn't be changed
          />

          <Input
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Input
            margin="normal"
            fullWidth
            id="firstName"
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
          />

          <Input
            margin="normal"
            fullWidth
            id="lastName"
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Input
            margin="normal"
            fullWidth
            id="companyName"
            label="Company Name"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
          />

          <Input
            margin="normal"
            fullWidth
            id="vatNumber"
            label="VAT Number"
            name="vatNumber"
            value={formData.vatNumber}
            onChange={handleChange}
            error={!!errors.vatNumber}
            helperText={errors.vatNumber || "Format: EU VAT number (e.g., DE123456789)"}
          />
        </Stack>

        <Typography variant="h6" gutterBottom>
          Service Tier
        </Typography>

        <ServiceTierSelect
          currentTier={formData.serviceTier}
          onTierChange={handleServiceTierChange}
          disabled={loading}
        />

        {user.role === "admin" && (
          <Typography variant="subtitle2" color="text.secondary">
            Role:{" "}
            <span style={{ textTransform: "capitalize" }}>{user.role}</span>
          </Typography>
        )}
      </Stack>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Profile</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your profile? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openResetPasswordDialog}
        onClose={handleCancelResetPassword}
        aria-labelledby="reset-password-dialog-title"
        aria-describedby="reset-password-dialog-description"
      >
        <DialogTitle id="reset-password-dialog-title">
          Reset Password
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset your password? You will receive an
            email with instructions to create a new password.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelResetPassword} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmResetPassword}
            color="primary"
            variant="contained"
            autoFocus
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          type="button"
          variant="outlined"
          color="error"
          onClick={handleDeleteProfile}
          disabled={loading}
          startIcon={<DeleteOutline />}
          sx={{ marginRight: "auto" }}
        >
          Delete Profile
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          onClick={handleResetPassword}
          disabled={loading}
          startIcon={<LockReset />}
        >
          Reset Password
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? "Updating..." : "Save Changes"}
        </Button>
      </Box>
    </Box>
  );
}

export default ProfileForm;
